import React, { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { WeaveTheme } from '@weave/theme-original';
import { DropdownField, FormRow, genUID, Text, useFormField } from '@weave/design-system';
import { useAlert } from '@weave/alert-system';
import { CustomAxios } from '../../../redux/axios';
import {
  fetchSyncAppMetadata,
  IntegrationFieldsModel,
  selectIntegrationsMetadata,
} from '../../../redux/actions';
import { selectCurrentLocationId } from '../../../redux/actions/location';
import { SyncAppSettingsQueries } from '../../../apis/sync-app-locations/queries';
import { OfficeOption } from '../../generic/office-multi-select/office-multi-select.component';
import { SettingsCard } from './settings-card.component';
import { SettingsFields } from './settings-fields.component';
import { AlertProps } from './sync-app-settings-page.component';

export const formRowSingleChildMargin = (theme: WeaveTheme) => css`
  margin-right: calc(${theme.spacing(2)} - 1px);
`;

type Props = {
  showAlertModal: (obj: AlertProps) => void;
};

export const CreateCloudSyncApp = ({ showAlertModal }: Props) => {
  const [creatingSyncApp, setCreatingSyncApp] = useState<boolean>(false);
  const [formKey, setFormKey] = useState<string>('integrations');
  const [formFields, setFormFields] = useState<{ [key: string]: any }>({});
  const [selectedIntegrationMetadata, setSelectedIntegrationMetadata] = useState<
    IntegrationFieldsModel[]
  >([]);
  const [submittedData, setSubmittedData] = useState<Record<string, string> | null>(null);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);
  const integrationsMetadata = useSelector(selectIntegrationsMetadata);
  const integrations = Object.keys(integrationsMetadata);
  const integrationProps = useFormField({ type: 'dropdown', required: true });
  const currentLocationId = useSelector(selectCurrentLocationId);
  const dispatch = useDispatch();
  const alert = useAlert();

  const {
    data: fetchLocationsResponse,
    isFetching: isFetchingOffices,
    refetch: refetchOffices,
  } = SyncAppSettingsQueries.useLocationsDuringOnboardingQuery(
    currentLocationId,
    integrationProps.value ?? '',
    submittedData ?? {},
    {
      enabled: !!submittedData && !!integrationProps.value && !!currentLocationId,
      onSuccess: (response) => {
        const officeCount = response?.locations?.length ?? 0;
        const supported = response?.locationSelectionSupported ?? true;
        alert.success(
          supported
            ? `Fetched ${officeCount} office${
                officeCount === 1 ? '' : 's'
              }. Select at least one to continue.`
            : 'Offices fetched. This integration does not require office selection.'
        );
      },
      onError: (error) => {
        alert.error(
          error?.message ??
            'Failed to fetch offices. Verify the credentials and try again.'
        );
      },
    }
  );

  const offices: OfficeOption[] = (fetchLocationsResponse?.locations ?? []).map(
    (location) => ({
      id: location.id ?? '',
      name: location.name ?? '',
      address: location.address ?? '',
    })
  );
  const hasFetchedOffices = !!fetchLocationsResponse;
  const locationSelectionSupported =
    fetchLocationsResponse?.locationSelectionSupported ?? false;

  useEffect(() => {
    if (!integrations.length) {
      dispatch(fetchSyncAppMetadata());
    }
  }, [integrations.length]);

  useEffect(() => {
    setFormFields({});
    setSubmittedData(null);
    setSelectedOfficeIds([]);
    const integration = integrationProps.value;
    if (integration) {
      // PDDS_DENTICON returns a `denticon_offices` field from the API that
      // we don't want to expose in the UI; offices are selected separately.
      const metadata = integrationsMetadata[integration].filter(
        (fieldMeta) =>
          !(integration === 'PDDS_DENTICON' && fieldMeta.name === 'denticon_offices')
      );
      const fields = metadata.reduce(
        (obj, fieldMeta) => ({
          ...obj,
          [`${fieldMeta.name}`]: {
            type: fieldMeta.valueType !== 'PASSWORD' ? 'text' : 'password',
            required: true,
            ...(fieldMeta.valueType === 'PASSWORD' && { minChars: 2 }),
          },
        }),
        {}
      );
      setFormFields(fields);
      setSelectedIntegrationMetadata(metadata);
      setFormKey(`${integrationProps.value}-${genUID()}`);
    }
  }, [integrationProps.value]);

  const handleFetchOffices = (values: Record<string, string>) => {
    setSelectedOfficeIds([]);
    // Force a fresh API call every time. If the form values are unchanged the
    // queryKey won't change, so trigger refetch() explicitly; otherwise updating
    // submittedData changes the queryKey and react-query will fetch on its own.
    if (submittedData && isEqual(submittedData, values)) {
      refetchOffices();
    } else {
      setSubmittedData(values);
    }
  };

  const handleSubmit = async (values: { [key: string]: any }) => {
    try {
      setCreatingSyncApp(true);
      const submitPayload = {
        integration: integrationProps.value,
        data: {
          ...values,
          ...(locationSelectionSupported && {
            whitelist_locations: JSON.stringify(selectedOfficeIds),
          }),
        },
        locationId: currentLocationId,
      };
      await CustomAxios.post('/support/v1/syncapp/provision/', submitPayload);
      showAlertModal({
        title: 'Success!',
        description:
          'The cloud sync app has been successfully created. Please allow a few hours for all the data to sync to the Weave client.',
      });
      setSubmittedData(null);
      setSelectedOfficeIds([]);
      setFormKey(`${integrationProps.value}-${genUID()}`);
    } catch (error: any) {
      console.error(error);
      showAlertModal({
        title: 'Error',
        description:
          error.response?.data?.error ?? 'Something went wrong. Try again later.',
      });
    } finally {
      setCreatingSyncApp(false);
    }
  };

  return (
    <SettingsCard title="Create Cloud Sync App">
      <>
        <Text
          color="light"
          css={(theme: WeaveTheme) => css`
            margin: ${theme.spacing(0, 0, 3)};
          `}
        >
          Create a new cloud sync app integration below
        </Text>

        <FormRow
          cols={[50]}
          css={css`
            width: 800px;
          `}
        >
          <DropdownField
            {...integrationProps}
            name="integration"
            label="Integration"
            css={formRowSingleChildMargin}
          >
            {integrations.sort().map((integration, index) => (
              <DropdownField.Option value={integration} key={index}>
                {integration}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </FormRow>

        <hr
          css={(theme: WeaveTheme) => css`
            width: 100%;
            border-top: 1px solid ${theme.colors.gray[300]};
            padding-bottom: ${theme.spacing(3)};
            margin: 0;
          `}
        />

        <SettingsFields
          fields={formFields}
          fieldsMetadata={selectedIntegrationMetadata}
          formComplete={!creatingSyncApp && !!integrationProps.value}
          onClick={handleSubmit}
          offices={offices}
          selectedOfficeIds={selectedOfficeIds}
          onOfficeSelectionChange={setSelectedOfficeIds}
          onFetchOffices={handleFetchOffices}
          isFetchingOffices={isFetchingOffices}
          hasFetchedOffices={hasFetchedOffices}
          lastFetchedValues={submittedData}
          locationSelectionSupported={locationSelectionSupported}
          key={formKey}
        />
      </>
    </SettingsCard>
  );
};
